import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/api";
import Layout from "../components/Layout";

export default function ViewInterviews() {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInterviews = async () => {
      try {
        const res = await api.get("/interviews");

        // 🛠 Fix: Handle both { interviews: [...] } and [...] directly
        const data = res.data?.interviews || res.data;
        if (Array.isArray(data)) {
          setInterviews(data);
        } else {
          console.error("Unexpected API response:", res.data);
          setError("Unexpected response from server.");
        }
      } catch (err) {
        console.error("Error fetching interviews:", err);
        setError("Failed to load interviews. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchInterviews();
  }, []);

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this interview?"
    );
    if (!confirmDelete) return;

    try {
      await api.delete(`/interviews/${id}`);
      setInterviews((prev) => prev.filter((iv) => iv.id !== id));
    } catch (err) {
      console.error("Delete failed:", err);
      alert("Failed to delete interview. Please try again.");
    }
  };

  const formatDate = (dateStr) => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(dateStr));
  };

  const getStatusBadge = (status) => {
    let colorClasses = "bg-gray-100 text-gray-800 border";
    switch (status?.toLowerCase()) {
      case "completed":
        colorClasses = "bg-green-50 text-green-800 border border-green-300";
        break;
      case "pending":
        colorClasses = "bg-yellow-50 text-yellow-800 border border-yellow-300";
        break;
      case "cancelled":
        colorClasses = "bg-red-50 text-red-800 border border-red-300";
        break;
      case "scheduled":
        colorClasses = "bg-blue-50 text-blue-800 border border-blue-300";
        break;
      default:
        colorClasses = "bg-gray-100 text-gray-800 border";
    }
    return (
      <span
        className={`inline-flex items-center px-3 py-0.5 rounded-full text-sm font-medium ${colorClasses}`}
      >
        {status || "Unknown"}
      </span>
    );
  };

  return (
    <Layout>
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900">All Interviews</h2>
        <button
          onClick={() => navigate("/add")}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md shadow hover:shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 transition"
        >
          + Add Interview
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-48">
          <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-blue-500"></div>
          <span className="ml-3 text-gray-500">Loading interviews...</span>
        </div>
      ) : error ? (
        <p className="text-red-500 text-center">{error}</p>
      ) : interviews.length === 0 ? (
        <p className="text-gray-500 text-center">
          No interviews found. Start by adding one!
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {interviews.map((iv) => (
            <div
              key={iv.id}
              className="bg-white rounded-lg shadow hover:shadow-lg transition p-5 border border-gray-200"
            >
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-xl font-medium text-gray-900">
                  {iv.company}
                </h3>
                {getStatusBadge(iv.status)}
              </div>
              <p className="text-gray-600">
                <span className="font-medium">Position:</span> {iv.position}
              </p>
              <p className="text-gray-600 mb-3">
                <span className="font-medium">Date:</span> {formatDate(iv.date)}
              </p>
              <div className="flex space-x-2 mt-3">
                <button
                  onClick={() => navigate(`/interviews/${iv.id}`)}
                  className="flex-1 px-3 py-1.5 bg-blue-500 text-white text-sm font-medium rounded-md hover:bg-blue-600 transition"
                >
                  View
                </button>
                <button
                  onClick={() => navigate(`/interviews/edit/${iv.id}`)}
                  className="flex-1 px-3 py-1.5 bg-amber-500 text-white text-sm font-medium rounded-md hover:bg-amber-600 transition"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(iv.id)}
                  className="flex-1 px-3 py-1.5 bg-red-500 text-white text-sm font-medium rounded-md hover:bg-red-600 transition"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}
